import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { OnboardingService, OnboardingStatus } from '../../services/onboarding.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-onboarding-finalize',
    templateUrl: './onboarding-finalize.component.html',
    styleUrls: ['./onboarding-finalize.component.scss'],
    standalone: false
})
export class OnboardingFinalizeComponent implements OnInit, OnDestroy {
  status: OnboardingStatus | null = null;
  canFinalize = false;
  isTransferCompany = false;
  // defaultPlus has no salon/table (or transfer) setup step — the setup row
  // and "back to setup" button are meaningless for it.
  isDefaultPlusCompany = false;
  setupRoute = '/pages/table_mangement';
  isProcessing = false;
  progress = 0;
  currentStage = '';
  errorMessage = '';
  private progressTimer?: ReturnType<typeof setInterval>;
  private statusSub?: Subscription;

  constructor(
    private onboardingService: OnboardingService,
    private router: Router,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.statusSub = this.onboardingService.status$.subscribe((status) => {
      if (!status) {
        return;
      }
      this.status = status;
      this.canFinalize = !!status.can_finalize;
      this.isTransferCompany = this.onboardingService.isTransferCompany();
      this.isDefaultPlusCompany = this.onboardingService.isDefaultPlusCompany();
      this.setupRoute = this.onboardingService.getSetupRoute();
    });

    this.onboardingService.getStatus().subscribe({
      error: () => {
        this.errorMessage = this.translate.instant(
          'ONBOARDING_FINALIZE.ERRORS.LOAD_STATUS'
        );
      }
    });
  }

  ngOnDestroy(): void {
    this.stopProgress();
    this.statusSub?.unsubscribe();
  }

  finalize(): void {
    if (!this.canFinalize || this.isProcessing) {
      return;
    }

    this.isProcessing = true;
    this.errorMessage = '';
    this.progress = 5;
    this.currentStage = this.translate.instant('ONBOARDING_FINALIZE.PROGRESS.INITIALIZING');
    this.startProgress();

    this.onboardingService.finalize().subscribe({
      next: () => {
        this.stopProgress();
        this.progress = 100;
        this.currentStage = this.translate.instant('ONBOARDING_FINALIZE.PROGRESS.COMPLETE');
        this.onboardingService.refreshStatus().subscribe({
          next: () => {
            setTimeout(() => this.router.navigate(['/dashboard']), 1200);
          },
          error: () => {
            setTimeout(() => this.router.navigate(['/dashboard']), 1200);
          }
        });
      },
      error: (error) => {
        this.stopProgress();
        this.errorMessage =
          error?.error?.message ||
          error?.message ||
          this.translate.instant('ONBOARDING_FINALIZE.ERRORS.FINALIZE_FAILED');
      }
    });
  }

  retry(): void {
    this.finalize();
  }

  getSetupStepLabel(): string {
    return this.translate.instant(
      this.isTransferCompany
        ? 'ONBOARDING_FINALIZE.STEPS.TRANSFER_SETUP'
        : 'ONBOARDING_FINALIZE.STEPS.SALON_TABLE_SETUP'
    );
  }

  getSetupStepStatus(currentStatus: OnboardingStatus): string {
    return currentStatus.steps.salon_table_setup.completed
      ? this.translate.instant('ONBOARDING_FINALIZE.STATUS.COMPLETE')
      : this.translate.instant('ONBOARDING_FINALIZE.STATUS.INCOMPLETE');
  }

  getPreviousStepsMessage(): string {
    return this.translate.instant(
      'ONBOARDING_FINALIZE.MESSAGES.PREVIOUS_STEPS_REQUIRED'
    );
  }

  private startProgress(): void {
    const stages = [
      {
        progress: 35,
        label: this.translate.instant('ONBOARDING_FINALIZE.PROGRESS.GENERATING_CONFIG')
      },
      {
        progress: 70,
        label: this.translate.instant('ONBOARDING_FINALIZE.PROGRESS.STARTING_ENGINE')
      },
      {
        progress: 90,
        label: this.translate.instant('ONBOARDING_FINALIZE.PROGRESS.PROCESSING_DOCUMENTS')
      }
    ];

    let stageIndex = 0;
    this.progressTimer = setInterval(() => {
      if (!this.isProcessing) {
        this.stopProgress();
        return;
      }

      const stage = stages[Math.min(stageIndex, stages.length - 1)];
      if (this.progress < stage.progress) {
        this.progress = stage.progress;
      }
      this.currentStage = stage.label;

      if (stageIndex < stages.length - 1) {
        stageIndex += 1;
      }
    }, 3000);
  }

  private stopProgress(): void {
    if (this.progressTimer) {
      clearInterval(this.progressTimer);
      this.progressTimer = undefined;
    }
    this.isProcessing = false;
  }
}
